<template>
	<v-menu
		v-model="menu"
		location="bottom left"
		origin="top left"
		transition="scale-transition"
		data-test="admin-menu-list"
		:close-on-content-click="false"
	>
		<template #activator="{ props }">
			<v-btn v-bind="props" stacked data-test="admin-menu-btn">
				<v-icon icon="fas fa-tools"></v-icon>
			</v-btn>
		</template>
		<template #default>
			<v-list
				data-test="admin-menu-items"
				slim
				density="compact"
				:items="items"
				:item-props="itemProps"
				min-width="300"
				base-color="primary"
				color="secondary"
			>
			</v-list>
		</template>
	</v-menu>
</template>
<script setup lang="ts">
import { useSubscription } from '@/composables/subscription'
import { useNavStore } from '@/stores/system'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RouteRecordRaw } from 'vue-router'

const menu = ref(false)

const { nav } = storeToRefs(useNavStore())

const { mapRouteToListItem } = useNavStore()

const { t } = useI18n()
const { checkIfSubscribedTo } = useSubscription()

const isRouteLocked = (route: RouteRecordRaw) => {
	if (!route?.meta?.subs) return false
	return !route.meta.subs.every((sub) => checkIfSubscribedTo(sub).allowed)
}

const navItemsFormatted = computed(() =>
	nav.value
		? mapRouteToListItem(
				nav.value.filter((items) => items.name === 'administration'),
				(item, hasChildren) => {
					return {
						title: t(item.meta?.title) ?? '',
						props: {
							prependIcon: item.meta?.icon,
							appendIcon:
								!hasChildren && isRouteLocked(item)
									? 'fa-solid fa-lock'
									: undefined,
							to: item?.name && !hasChildren ? { name: item.name } : undefined,
							disabled: item.name === undefined || isRouteLocked(item),
							onClick:
								item?.name && !hasChildren
									? () => {
											menu.value = false
										}
									: undefined,
						},
						meta: item.meta,
						'data-test': item.name,
					}
				}
			).flatMap((items) => items.children)
		: []
)

const itemProps = (item: any) => ({
	...item.props,
	'data-test': item['data-test'] ? `nav-${item['data-test']}` : undefined,
})

const items = computed(() => [
	{
		title: t('common.administration'),
		type: 'header',
	},
	{
		type: 'divider',
	},
	...navItemsFormatted.value,
])
</script>
<style></style>
